import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Pagination,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatDate, formatDateTimeMonth } from "../../../../../../../../../utils/functions";
import {
  AccountCircleOutlined,
  HelpOutline,
  WarningAmber,
} from "@mui/icons-material";
import { Alert } from "../../../../../../../../../api/homeSiteAlerts/types";
import { Exception } from "../../../../../../../../../api/homeSiteExceptions/types";
import { useCreateExceptions } from "../../../../../../../../../reactQuery/homeSites/exceptions/homeSiteExceptions";

interface AlertsListProps {
  alerts: Alert[];
  page: number;
  itemsPerPage: number;
  updateAlert: (alert: Partial<Alert>) => void;
  createException: (exception: Exception) => void;
  setActivePage: (page: number) => void;
  homeSiteId: string;
  userId: string;
  title: string;
  mileStoneName: string;
  divisionCode: string;
  conciergeEmail: string;
  showSnackbar: (
    message: string,
    severity: "success" | "error" | "info"
  ) => void;
}

const AlertsList: React.FC<AlertsListProps> = ({
  alerts,
  page,
  itemsPerPage,
  updateAlert,
  setActivePage,
  homeSiteId,
  userId,
  title,
  mileStoneName,
  divisionCode,
  conciergeEmail,
  showSnackbar,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentAlertId, setCurrentAlertId] = React.useState<string | null>(
    null
  );

  const { mutate: createException } = useCreateExceptions(divisionCode,homeSiteId);

  const alertsToDisplay = alerts.length <= itemsPerPage ? alerts : alerts.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.ceil(alerts.length / itemsPerPage);

  // Move state update to useEffect to avoid updating state during render
  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setActivePage(totalPages);
    }
  }, [page, totalPages, setActivePage]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    alertId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentAlertId(alertId);
    getAlldetailsOfSelectedAlert(alertId);
  };

  const getAlldetailsOfSelectedAlert = (alertId: string) => {
    const selectedAlert = alerts.find((alert) => alert.id === alertId);
    if (selectedAlert) {
      console.log("Selected Alert Details:", selectedAlert);
      return selectedAlert;
    } else {
      console.log(`No alert found with id: ${alertId}`);
      return null;
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentAlertId(null);
  };

  const handleMarkResolved = (status: string) => {
    if (currentAlertId) {
      updateAlert({
        id: currentAlertId,
        homeSiteId: homeSiteId,
        status: status,
        updatedBy: userId,
        divisionCode: divisionCode,
      });
    }
    handleMenuClose();
    showSnackbar("Alert dismissed successfully!", "success");
  };

  const handleMarktoExceptionCreation = () => {
    if (currentAlertId) {
      handleMarkResolved("exception");
      const alertDetails = getAlldetailsOfSelectedAlert(currentAlertId);
      if (alertDetails) {
        createException({
          homeSiteId: homeSiteId,
          divisionCode: divisionCode,
          mileStoneName: alertDetails.mileStoneName,
          exceptionName: alertDetails.alertName,
          exceptionType: "Converted Risk",
          description: alertDetails.description,
          conciergeEmail: conciergeEmail,
          createdBy: userId,
        });
      }
    }
    handleMenuClose();
    showSnackbar("Alert converted to exception!", "success");
  };

  return (
    <Box>
      {alertsToDisplay.map((alert) => (
        <Card
          key={alert.id}
          variant="outlined"
          style={{
            marginBottom: "8px",
            backgroundColor: "#FFF8E1",
            position: "relative",
            borderColor: "#FFE186",
            borderRadius: "4px",
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                backgroundColor: "#C58026", // Background color
                color: "white", // Text color
                fontWeight: "bold", // Bold text
                fontSize: "12px", // Font size
                padding: "4px 12px", // Padding for the pill shape
                borderRadius: "16px", // Rounded corners
                display: "inline-block", // Inline-block for text alignment
              }}
            >
              {alert.mileStoneName}
            </Typography>
            <Box display="flex" justifyContent="space-between" marginTop="8px">
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                {alert.alertName}
              </Typography>
              <Tooltip
                title="Additional Information - Coming Soon"
                arrow
                placement="right"
              >
                <HelpOutline style={{ fontSize: "14px" }} />
              </Tooltip>
            </Box>
            <Typography variant="body1" sx={{ fontSize: "14px" }}>
              {alert.description}
            </Typography>
            <Box display="flex" justifyContent="space-between" marginTop="8px">
              <Box display="flex" alignItems="center">
                <AccountCircleOutlined />
                <Typography variant="caption" marginLeft="8px">
                  {alert.createdBy}
                </Typography>
              </Box>
              <Typography variant="caption">
                {formatDateTimeMonth(alert.createdOnDate)}
              </Typography>
            </Box>
            {alert.status ? (
              <IconButton
                onClick={(event) => handleMenuOpen(event, alert.id)}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "4px",
                  color: "black",
                }}
              >
                <Tooltip title="Menu" arrow placement="left">
                  <MoreVertIcon />
                </Tooltip>
              </IconButton>
            ) : (
              <IconButton
                onClick={(event) => handleMenuOpen(event, alert.id)}
                style={{ position: "absolute", top: "8px", right: "8px" }}
              >
                <CheckCircleIcon sx={{ color: "green" }} />
              </IconButton>
            )}
          </CardContent>
        </Card>
      ))}
      {alerts.length > itemsPerPage && (
        <Box marginTop={2} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setActivePage(value)}
            color="primary"
          />
        </Box>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MuiMenuItem onClick={() => handleMarkResolved("resolved")}>
          <ListItemIcon>
            <CheckCircleIcon sx={{ color: "green" }} />
          </ListItemIcon>
          Dismiss
        </MuiMenuItem>
        <MuiMenuItem onClick={handleMarktoExceptionCreation}>
          <ListItemIcon>
            <WarningAmber sx={{ color: "red" }} />
          </ListItemIcon>
          Convert to Exception
        </MuiMenuItem>
      </Menu>
    </Box>
  );
};

export default AlertsList;
